import React from 'react';
import { motion } from 'framer-motion';
import { Link2Icon, QrCodeIcon, SmartphoneIcon, UserPlusIcon, CheckIcon } from 'lucide-react';
import { useLaunch } from '../contexts/LaunchContext';
const webPoints = ['Open the invite link in any browser', 'Take part without installing the app', 'See the kitty, its expenses and who owes what', 'Claim your name if someone added you already'];
const appPoints = ['Every kitty you are part of, plus My Balance', 'Invite from contacts, or by link, code and QR', 'Receipts, notifications and settlement records', 'Admin tools for the person running the kitty'];
export function WaysToJoin() {
  const {
    isPublicLaunch
  } = useLaunch();
  const scrollToCta = () => document.getElementById('get-started')?.scrollIntoView({
    behavior: 'smooth'
  });
  return <section id="ways-to-join" className="py-24 lg:py-32 bg-white" data-id="element-329" data-mp="l5pikxt1xji9">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-330" data-mp="p4ybk0g9pdcr">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="max-w-2xl mb-12 lg:mb-16" data-id="element-331" data-mp="1qfc5o6ithho5">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-kitty-dark tracking-tight mb-4 leading-tight" data-id="element-332" data-mp="1gq0wkjvxkd09">
            Everyone can join, however they want
          </h2>
          <p className="text-lg text-gray-500" data-id="element-333" data-mp="29cvhtj0knges">
            One person starts the kitty. Everyone else joins from a link in
            their browser or in the app, and the group can start tracking spends
            straight away.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6" data-id="element-334" data-mp="1uhfgqu1kqk8w">
          {/* Primary: browser join */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-3 rounded-3xl bg-kitty-yellow-50 border border-kitty-yellow-200 p-8 lg:p-10" data-id="element-335" data-mp="o0lpg57dz49m">
            <div className="flex items-center gap-3 mb-6" data-id="element-336" data-mp="3b7zvpfkfhms1">
              <div className="w-11 h-11 rounded-2xl bg-kitty-yellow flex items-center justify-center" data-id="element-337" data-mp="2x9s13eav6xk1">
                <Link2Icon className="w-5 h-5 text-kitty-dark" aria-hidden="true" data-id="element-338" data-mp="328cv2vziwl01" />
              </div>
              <div data-id="element-339" data-mp="2nifbktcjgjh6">
                <p className="text-xs font-semibold uppercase tracking-widest text-kitty-yellow-700" data-id="element-340" data-mp="2nhvq52nojcye">
                  Fastest way in
                </p>
                <h3 className="text-2xl font-extrabold text-kitty-dark" data-id="element-341" data-mp="2ekbnbpa658lo">
                  Join from your browser
                </h3>
              </div>
            </div>

            <ul className="space-y-3 mb-8" data-id="element-342" data-mp="24kchi054vwci">
              {webPoints.map(point => <li key={point} className="flex items-start gap-3" data-id="element-343" data-mp="29jnyna9ex3um">
                  <div className="mt-0.5 w-5 h-5 flex-shrink-0 rounded-full bg-kitty-dark flex items-center justify-center" data-id="element-344" data-mp="29m1q794vvxxf">
                    <CheckIcon className="w-3 h-3 text-kitty-yellow" aria-hidden="true" data-id="element-345" data-mp="1lwtsj0hsjwb4" />
                  </div>
                  <span className="text-base text-gray-800 font-medium" data-id="element-346" data-mp="1ztyw8tu18j37">
                    {point}
                  </span>
                </li>)}
            </ul>

            <div className="flex flex-wrap items-center gap-4 text-sm font-semibold text-kitty-dark" data-id="element-347" data-mp="28v6u5asmzhwr">
              <span className="inline-flex items-center gap-2" data-id="element-348" data-mp="24v656w7wvobg">
                <QrCodeIcon className="w-4 h-4" aria-hidden="true" data-id="element-349" data-mp="2ilroun56w2ae" />
                Link, code or QR
              </span>
              <span className="inline-flex items-center gap-2" data-id="element-350" data-mp="2do9ohl58mve0">
                <UserPlusIcon className="w-4 h-4" aria-hidden="true" data-id="element-351" data-mp="2sk7hiujv7jm0" />
                Name-only members welcome
              </span>
            </div>
          </motion.div>

          {/* Secondary: the app */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-2 rounded-3xl bg-gray-950 p-8 lg:p-10 text-white flex flex-col" data-id="element-352" data-mp="ky7eq59gakvf">
            <div className="w-11 h-11 rounded-2xl bg-white/[0.08] flex items-center justify-center mb-6" data-id="element-353" data-mp="znd99fxcfmwz">
              <SmartphoneIcon className="w-5 h-5 text-kitty-yellow" aria-hidden="true" data-id="element-354" data-mp="22hcqi1ipohj" />
            </div>
            <h3 className="text-2xl font-extrabold mb-2" data-id="element-355" data-mp="byat6cicy24l">The full app</h3>
            <p className="text-sm text-gray-400 mb-6" data-id="element-356" data-mp="gxdrle3u1a3l">
              {isPublicLaunch ? 'Available on iOS and Android.' : 'iOS and Android, in early access testing.'}
            </p>

            <ul className="space-y-3 mb-8" data-id="element-357" data-mp="1scapzelwpbnm">
              {appPoints.map(point => <li key={point} className="text-sm text-gray-300 leading-relaxed border-l-2 border-kitty-yellow/40 pl-3" data-id="element-358" data-mp="3cul9577ej1pb">
                  {point}
                </li>)}
            </ul>

            <button onClick={scrollToCta} className="mt-auto w-full bg-kitty-yellow text-kitty-dark py-3.5 rounded-full font-bold transition-colors duration-150 hover:bg-kitty-yellow-500 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-359" data-mp="33377otvw3rtj">
              {isPublicLaunch ? 'Download KittyPot' : 'Join the waitlist'}
            </button>
          </motion.div>
        </div>
      </div>
    </section>;
}